import Link from "next/link";
import { HubArrow, HubChevron } from "@/app/_components/conditions/hub/hub-icons";
import { HubBandPill, HubScoreRing } from "@/app/_components/conditions/hub/hub-parts";
import { AreaRowWhileCurrent } from "../AreaRankClock";
import type { AreaViewPick, AreaViewSportPanel } from "../area-view-types";
import { AreaDifficultyPill, AreaPill } from "./area-a-parts";
import { updatedAt } from "./area-g3-format";
import { G3_CARD, G3CardHeader } from "./g3-parts";

/**
 * "Picks for your plan" (G4 spec item 2): a few small cards per sport, computed from the same
 * week data as the board (area-view-model.ts `buildPicks`): label, spot, ring, one honest line
 * and a link. A pick built on today's ranked score is withdrawn with that row on the browser's
 * clock. Inside `data-nosnippet` (live scores and ranks). Client-safe: the on-demand sport panels render it from the panel route's payload.
 * Renders nothing when the sport has no picks.
 */

function PickCard({ pick, sport }: Readonly<{ pick: AreaViewPick; sport: string }>) {
  const aria = `${pick.label}: ${pick.name}${pick.closing ? ` (${pick.closing})` : ""}, ${pick.band.label}, ${pick.score100} out of 100 ${pick.caption}. ${pick.reason} Open the ${pick.shortName} forecast`;
  const ring = (
    <span className="flex w-[68px] shrink-0 flex-col items-center gap-0.5 md:w-auto">
      <HubScoreRing score100={pick.score100} size={52} numberClassName="text-[17px]" />
      <span className="whitespace-nowrap text-[11px] font-semibold text-[#566072]">{pick.caption}</span>
    </span>
  );
  const card = (
    <li data-area-pick={pick.key} className="border-t border-[#EEF0EB] first:border-t-0 md:border-t-0">
      <Link
        href={pick.href}
        aria-label={aria}
        className="group flex min-h-[88px] items-center gap-3 px-4 py-3 text-text-body no-underline transition-colors hover:bg-[#F4F8F7] md:h-full md:flex-col md:items-stretch md:gap-2 md:rounded-[16px] md:border md:border-line md:bg-[#F7F8F5] md:px-4 md:py-4"
      >
        <span className="md:hidden">{ring}</span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-teal-dark">{pick.label}</span>
          <span className="flex min-w-0 items-start justify-between gap-3">
            <span className="flex min-w-0 flex-col gap-1.5">
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="text-[16px] font-bold leading-[1.2] text-navy md:text-[18px]">{pick.shortName}</span>
                <HubBandPill label={pick.band.label} tone={pick.band.tone} />
                {pick.experiencedOnly && <AreaDifficultyPill label="Experienced only" tone="experienced" />}
                {pick.closing && <AreaPill tone={pick.closing.startsWith("Past") ? "neutral" : "pale"} className="text-[11.5px]">{pick.closing}</AreaPill>}
              </span>
            </span>
            <span className="hidden md:flex">{ring}</span>
          </span>
          <span className="text-[13.5px] leading-[1.45] text-text-body md:text-[14px]">{pick.reason}</span>
          <span className="hidden items-center gap-1.5 text-[14px] font-bold text-teal-dark md:inline-flex">
            Open forecast
            <HubArrow />
          </span>
        </span>
        <span aria-hidden="true" className="flex text-teal-dark md:hidden"><HubChevron direction="right" /></span>
      </Link>
    </li>
  );
  return pick.usesToday ? <AreaRowWhileCurrent sport={sport} slug={pick.slug}>{card}</AreaRowWhileCurrent> : card;
}

/** The picks card for one sport panel (the page's section holds the anchor). */
export function AreaPicksCard({ panel, timezone }: Readonly<{ panel: AreaViewSportPanel; timezone: string }>) {
  const picks = panel.picks;
  if (!picks || picks.items.length === 0) return null;
  const headingId = `area-picks-h-${panel.key}`;
  const updated = updatedAt(panel.updatedISO, timezone);
  const thing = panel.unit === "beaches" ? "beach" : panel.unit.replace(/s$/u, "");
  const footnote = [picks.weekendNote, `A pick is left out when no ${thing} qualifies, and one ${thing} can hold more than one pick.`]
    .filter(Boolean)
    .join(" ");
  return (
    // Live scores and ranks: kept out of search snippets, like the verdict and the board.
    <div data-area-picks={panel.key} data-nosnippet="" className={G3_CARD}>
      <G3CardHeader
        id={headingId}
        icon="bookmark"
        title="Picks for your plan"
        lead={`From the same ${panel.lowerName} week forecast as the board above.${updated ? ` ${updated}.` : ""}`}
      />
      <ul aria-labelledby={headingId} className="m-0 list-none p-0 md:grid md:grid-cols-2 md:gap-3 md:px-[22px] md:pt-1">
        {picks.items.map((pick) => <PickCard key={pick.key} pick={pick} sport={panel.key} />)}
      </ul>
      {picks.noBeginnerNote && (
        <p className="mx-4 mt-1 text-[12.5px] leading-[1.45] text-[#566072] md:mx-[22px] md:mt-2.5">{picks.noBeginnerNote}</p>
      )}
      <p className="px-4 pb-4 pt-2.5 text-[12px] leading-[1.45] text-[#566072] md:px-[22px] md:pb-5 md:pt-3.5 md:text-[12.5px]">{footnote}</p>
    </div>
  );
}
